
import React from 'react';
import { format } from 'date-fns';
import { CertificateMetadata } from '@/types/certification';
import { getCertificateHistory } from '@/utils/certification/certificateHistoryUtils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileCheck, FileText, Clock, CheckCircle2, RotateCw } from 'lucide-react';

interface CertificateHistoryTimelineProps {
  certificate: CertificateMetadata;
}

const CertificateHistoryTimeline: React.FC<CertificateHistoryTimelineProps> = ({ 
  certificate 
}) => {
  const history = getCertificateHistory(certificate.id);

  if (!history.length) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        No history available for this certificate.
      </div>
    );
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'issuance':
        return <FileCheck className="h-4 w-4" />;
      case 'status_change':
        return <Clock className="h-4 w-4" />;
      case 'verification':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'revision':
        return <FileText className="h-4 w-4" />;
      case 'renewal':
        return <RotateCw className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-4">
        {history.map((entry, index) => (
          <div key={index} className="flex gap-4 items-start">
            <div className="mt-1">
              {getActionIcon(entry.action)}
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{entry.description}</p>
                  <p className="text-sm text-muted-foreground">
                    By {entry.performedBy || 'System'}
                  </p>
                </div>
                <time className="text-sm text-muted-foreground">
                  {format(new Date(entry.timestamp), 'PPp')}
                </time>
              </div>
              {entry.details && (
                <div className="mt-2 text-sm bg-muted/50 rounded-md p-2">
                  {Object.entries(entry.details).map(([key, value]) => (
                    <div key={key} className="grid grid-cols-2">
                      <span className="text-muted-foreground capitalize">
                        {key.replace(/_/g, ' ')}:
                      </span>
                      <span>{String(value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default CertificateHistoryTimeline;
